import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, AlertCircle, ArrowLeft } from 'lucide-react';
import { getAuthUrl } from '../authConfig';
import { validateAccess } from '../api/authApi';

const VALID_EMAIL = 'altausuarios@valorizasm.com';
const VALID_PASS = 'Val0rizaAlta26!';

const parseHashParams = (hash) => {
  const params = {};
  const hashString = hash.startsWith('#') ? hash.substring(1) : hash;
  hashString.split('&').forEach(param => {
    const [key, value] = param.split('=');
    if (key && value) params[key] = decodeURIComponent(value);
  });
  return params;
};

const decodeJWT = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch { return null; }
};

// ─── Canvas Network Animation ────────────────────────────────────────────────
function NetworkCanvas() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const nodesRef = useRef([]);

  const initNodes = useCallback((w, h) => {
    const count = Math.floor((w * h) / 14000);
    return Array.from({ length: Math.min(count, 70) }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.45,
      vy: (Math.random() - 0.5) * 0.45,
      r: Math.random() * 2.4 + 1.2,
      pulse: Math.random() * Math.PI * 2,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      nodesRef.current = initNodes(canvas.width, canvas.height);
    };
    resize();
    window.addEventListener('resize', resize);

    const LINK_DIST = 155;
    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const nodes = nodesRef.current;
      nodes.forEach(n => {
        n.x += n.vx;
        n.y += n.vy;
        n.pulse += 0.025;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      });

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < LINK_DIST) {
            const alpha = (1 - dist / LINK_DIST) * 0.35;
            ctx.strokeStyle = `rgba(74,222,128,${alpha})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      nodes.forEach(n => {
        const pulse = 0.8 + Math.sin(n.pulse) * 0.2;
        const r = n.r * pulse;
        const gradient = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, r * 3.5);
        gradient.addColorStop(0, 'rgba(74,222,128,0.9)');
        gradient.addColorStop(0.5, 'rgba(34,197,94,0.4)');
        gradient.addColorStop(1, 'rgba(22,163,74,0)');
        ctx.beginPath();
        ctx.arc(n.x, n.y, r * 3.5, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(134,239,172,0.95)';
        ctx.fill();
      });

      animRef.current = requestAnimationFrame(draw);
    };
    animRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [initNodes]);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    />
  );
}

// ─── Floating Orbs ────────────────────────────────────────────────────────────
const orbs = [
  { w: 480, h: 480, top: '-15%', left: '-12%', dur: 22 },
  { w: 360, h: 360, top: '55%', left: '60%', dur: 28 },
  { w: 280, h: 280, top: '30%', left: '40%', dur: 18 },
];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmergencyLogin, setShowEmergencyLogin] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && (hash.includes('id_token=') || hash.includes('access_token='))) {
      handleAzureRedirect(hash);
    }
  }, []);

  const handleAzureRedirect = async (hash) => {
    try {
      setLoading(true);
      const params = parseHashParams(hash);
      const idToken = params.id_token;
      const state = params.state;
      const expectedState = sessionStorage.getItem('azure_auth_state');
      if (state !== expectedState) throw new Error('Estado de autenticación inválido. Posible ataque CSRF.');
      sessionStorage.removeItem('azure_auth_state');
      sessionStorage.removeItem('azure_auth_nonce');
      window.history.replaceState(null, '', window.location.pathname);
      if (!idToken) throw new Error('No se recibió token de identidad de Azure AD.');
      const userInfo = decodeJWT(idToken);
      if (!userInfo) throw new Error('No se pudo decodificar el token de identidad.');
      const userEmail = userInfo.email || userInfo.preferred_username || userInfo.upn;
      const userName = userInfo.name || userEmail;
      await validateAccess(userEmail, userName, 'sso');
      sessionStorage.setItem('valoriza_it_session', 'true');
      sessionStorage.setItem('valoriza_it_user', JSON.stringify({ name: userName, email: userEmail, method: 'sso', id_token: idToken }));
      onLogin();
    } catch (err) {
      setError(err.message || 'Error al procesar la autenticación de Microsoft.');
      setLoading(false);
    }
  };

  const handleSSOLogin = () => { window.location.href = getAuthUrl(); };

  const handleEmergencyLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Por favor, completa todos los campos.'); return; }
    setLoading(true);
    setError('');
    try {
      if (email !== VALID_EMAIL || password !== VALID_PASS) throw new Error('Credenciales incorrectas.');
      await validateAccess(email, 'Usuario de Emergencia', 'emergency');
      sessionStorage.setItem('valoriza_it_session', 'true');
      sessionStorage.setItem('valoriza_it_user', JSON.stringify({ email, method: 'emergency' }));
      onLogin();
    } catch (err) {
      setError(err.message || 'Error al validar acceso.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      background: '#030a05', overflow: 'hidden', position: 'relative'
    }}>
      <style>{`
        @keyframes orb-float {
          0%, 100% { transform: translate(0,0) scale(1); }
          33% { transform: translate(30px,-40px) scale(1.05); }
          66% { transform: translate(-20px,25px) scale(0.97); }
        }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes yoda-float {
          0%, 100% { transform: translateY(0px) rotate(-1deg); }
          50%       { transform: translateY(-14px) rotate(1deg); }
        }
        @keyframes shimmer {
          0% { opacity: 0.4; }
          50% { opacity: 0.9; }
          100% { opacity: 0.4; }
        }
        .input-field {
          width: 100%;
          height: 52px;
          padding: 0 16px;
          font-size: 14px;
          background: rgba(255,255,255,0.05);
          border: 1.5px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          color: #fff;
          outline: none;
          transition: border-color 0.2s, background 0.2s;
          box-sizing: border-box;
        }
        .input-field::placeholder { color: rgba(255,255,255,0.3); }
        .input-field:focus { border-color: #22c55e; background: rgba(34,197,94,0.07); }
        .sso-btn {
          width: 100%;
          height: 56px;
          background: linear-gradient(135deg, #16a34a, #22c55e);
          color: #fff;
          border: none;
          border-radius: 14px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          letter-spacing: 0.2px;
          box-shadow: 0 4px 24px rgba(34,197,94,0.35), 0 0 0 0 rgba(34,197,94,0.4);
          transition: transform 0.2s, box-shadow 0.2s;
          position: relative;
          overflow: hidden;
        }
        .sso-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.15), transparent);
          opacity: 0;
          transition: opacity 0.2s;
        }
        .sso-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(34,197,94,0.55); }
        .sso-btn:hover::after { opacity: 1; }
        .sso-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .submit-btn {
          width: 100%;
          height: 50px;
          background: rgba(34,197,94,0.15);
          color: #4ade80;
          border: 1.5px solid rgba(34,197,94,0.4);
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s, transform 0.2s;
        }
        .submit-btn:hover:not(:disabled) { background: rgba(34,197,94,0.25); transform: translateY(-1px); }
        .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        @media (max-width: 768px) {
          .left-panel { display: none !important; }
          .right-panel { width: 100% !important; }
        }
      `}</style>

      {/* ── LEFT PANEL ── */}
      <div
        className="left-panel"
        style={{
          flex: '1 1 55%', position: 'relative', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '56px'
        }}
      >
        {/* Canvas animation */}
        <NetworkCanvas />

        {/* Floating orbs for depth */}
        {orbs.map((o, i) => (
          <div key={i} style={{
            position: 'absolute', top: o.top, left: o.left,
            width: o.w, height: o.h, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(34,197,94,0.18) 0%, rgba(22,163,74,0.08) 50%, transparent 70%)',
            animation: `orb-float ${o.dur}s ease-in-out infinite`,
            animationDelay: `${i * 3}s`,
            pointerEvents: 'none',
            filter: 'blur(2px)',
          }} />
        ))}

        {/* Subtle grid */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(34,197,94,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(34,197,94,0.04) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />

        {/* Content overlay */}
        <div style={{ position: 'relative', zIndex: 10 }}>
          {/* Logo chip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px',
              background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
              borderRadius: '100px', padding: '8px 18px', marginBottom: '28px'
            }}
          >
            <div style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: '#4ade80', animation: 'shimmer 2s ease infinite',
              boxShadow: '0 0 6px #4ade80'
            }} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#4ade80', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              Portal IT · Valoriza
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            style={{
              fontSize: 'clamp(38px, 4.5vw, 62px)',
              fontWeight: 900,
              color: '#fff',
              lineHeight: 1.1,
              margin: '0 0 20px 0',
              letterSpacing: '-2px',
            }}
          >
            Alta de empleados<br />
            <span style={{
              background: 'linear-gradient(90deg, #4ade80, #22c55e, #86efac)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundSize: '200% 100%', animation: 'gradient-shift 4s ease infinite'
            }}>
              sin fricciones.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            style={{ fontSize: '16px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.7', maxWidth: '400px', margin: '0 0 40px 0' }}
          >
            Portal seguro para el alta de nuevos empleados. Integrado con Azure AD y Microsoft Intune para un onboarding fluido.
          </motion.p>

          {/* Feature pills */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}
          >
            {['🔐 ENS Alto', '☁️ Azure AD', '📱 Microsoft Intune'].map((tag, i) => (
              <span key={i} style={{
                padding: '7px 16px', borderRadius: '100px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.6)',
                letterSpacing: '0.3px'
              }}>
                {tag}
              </span>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div
        className="right-panel"
        style={{
          flex: '0 0 420px', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '32px 28px 0 28px', position: 'relative',
          background: 'rgba(0,0,0,0.4)',
          borderLeft: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(32px)',
          overflow: 'hidden',
        }}
      >
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: '100%', maxWidth: '360px' }}
        >
          {/* Card */}
          <div style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '24px',
            padding: '40px 36px',
            boxShadow: '0 32px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5, type: 'spring' }}
                style={{
                  width: '64px', height: '64px', margin: '0 auto 20px',
                  background: 'linear-gradient(135deg, #16a34a, #22c55e)',
                  borderRadius: '18px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 8px 28px rgba(34,197,94,0.4)',
                  fontSize: '28px'
                }}
              >
                V
              </motion.div>
              <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#fff', margin: '0 0 8px', letterSpacing: '-0.5px' }}>
                Bienvenido
              </h2>
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)', margin: 0 }}>
                Accede con tu cuenta corporativa
              </p>
            </div>

            {/* SSO Button (main path) */}
            <AnimatePresence mode="wait">
              {!showEmergencyLogin ? (
                <motion.div
                  key="sso"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  <button
                    className="sso-btn"
                    onClick={handleSSOLogin}
                    disabled={loading}
                    data-testid="sso-login-button"
                  >
                    {loading ? (
                      <>
                        <svg style={{ animation: 'spin 1s linear infinite', width: '18px', height: '18px', flexShrink: 0 }} viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/>
                          <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                        </svg>
                        Iniciando sesión...
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 21 21" fill="none" style={{ flexShrink: 0 }}>
                          <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                          <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                          <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                          <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                        </svg>
                        Iniciar sesión con Microsoft
                      </>
                    )}
                  </button>

                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0'
                  }}>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.25)', whiteSpace: 'nowrap' }}>acceso alternativo</span>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
                  </div>

                  <button
                    data-testid="show-emergency-login-button"
                    onClick={() => setShowEmergencyLogin(true)}
                    style={{
                      width: '100%', background: 'none', border: '1.5px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px', color: 'rgba(255,255,255,0.4)', fontSize: '13px', fontWeight: 600,
                      padding: '12px', cursor: 'pointer', transition: 'border-color 0.2s, color 0.2s',
                      letterSpacing: '0.2px'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
                  >
                    Acceso de emergencia
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="emergency"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                  onSubmit={handleEmergencyLogin}
                >
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: '8px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                      Email corporativo
                    </label>
                    <input
                      type="email"
                      className="input-field"
                      data-testid="login-email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="usuario@valorizasm.com"
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: '8px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                      Contraseña
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPass ? 'text' : 'password'}
                        className="input-field"
                        data-testid="login-password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="••••••••"
                        style={{ paddingRight: '48px' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass(!showPass)}
                        style={{
                          position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.35)',
                          display: 'flex', padding: '4px', transition: 'color 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = '#4ade80'}
                        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}
                      >
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        style={{
                          background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                          borderRadius: '10px', padding: '10px 14px', marginBottom: '16px',
                          fontSize: '13px', color: '#fca5a5', display: 'flex', alignItems: 'flex-start', gap: '8px'
                        }}
                      >
                        <AlertCircle size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <button
                    type="submit"
                    className="submit-btn"
                    data-testid="login-submit"
                    disabled={loading}
                    style={{ marginBottom: '12px' }}
                  >
                    {loading ? 'Verificando...' : 'Acceder'}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setShowEmergencyLogin(false); setError(''); setEmail(''); setPassword(''); }}
                    style={{
                      width: '100%', background: 'none', border: 'none',
                      color: 'rgba(255,255,255,0.35)', fontSize: '13px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      padding: '8px', transition: 'color 0.2s', fontWeight: 500
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}
                  >
                    <ArrowLeft size={14} />
                    Volver a SSO
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <p style={{ textAlign: 'center', fontSize: '11px', color: 'rgba(255,255,255,0.2)', marginTop: '20px', lineHeight: 1.6 }}>
            Conexión segura ENS Alto<br />
            © 2026 Valoriza Servicios Medioambientales
          </p>
        </motion.div>

        {/* Maestro IT — Maldonado Yoda */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          style={{
            marginTop: '18px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <div style={{
            fontSize: '10px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase',
            color: 'rgba(74,222,128,0.5)', marginBottom: '6px'
          }}>
            Maestro IT
          </div>
          <img
            src="/MaldonadoYoda.png"
            alt="Maestro IT"
            style={{
              width: '260px',
              display: 'block',
              animation: 'yoda-float 5s ease-in-out infinite',
              filter: 'drop-shadow(0 0 28px rgba(74,222,128,0.25))',
              mixBlendMode: 'lighten',
            }}
          />
        </motion.div>
      </div>
    </div>
  );
}
